<template>
    <div class="calendar-button"
        v-bind:class="{
            'active': openMenu,
            'calendar-button--open': openMenu,
            'calendar-button--applied': isDateSelected,
        }">
        <div class="calendar-button__main" v-on:click="handleClick">
            <span class="calendar-button__icon">
                <icon-calendar />
            </span>
            <span class="calendar-button__period" v-if="isDateSelected">
                {{ period }}
            </span>
        </div>
        <div class="calendar-button__reset" v-if="isDateSelected" v-on:click.stop="resetDate">
            <icon-close />
        </div>
    </div>
</template>

<script>
import IconCalendar from '@img/icons/lucide/calendar-search.svg?vue';
import IconClose from '@img/icons/lucide/x.svg?vue';
import { mapState } from 'vuex';

export default {
    props: ['toggleMenu', 'openMenu'],
    computed: {
        isDateSelected() {
            return Boolean(this.datePickerFilter[0]);
        },
        period() {
            const firstDate = this.formatTimestamp(this.datePickerFilter[0]);
            const secondDate = this.formatTimestamp(this.datePickerFilter[1] - 1);

            return firstDate === secondDate ? firstDate : `${firstDate} – ${secondDate}`;
        },
        ...mapState(['datePickerFilter']),
    },
    methods: {
        handleClick() {
            this.toggleMenu();
        },
        resetDate() {
            this.$store.commit('resetDatePickerFilter');
        },
        formatTimestamp(ts) {
            const date = new Date(ts * 1000);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);

            if (this.isMobile) return `${day}.${month}`;
            return `${day}.${month}.${year}`;
        },
    },
    components: {
        IconCalendar,
        IconClose,
    },
};
</script>

<style lang="scss">
.calendar-button {
    display: inline-flex;
    align-items: center;
    position: relative;
    height: 36px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.01em;
    color: var(--body-muted-text-color);
    user-select: none;
    z-index: 90;
    transition: color .15s ease, background-color .15s ease, transform .1s ease;

    &:hover {
        color: var(--body-text-color);
        background: var(--icon-button-hover-background);
    }
    &:active {
        transform: scale(.96);
    }

    &__main {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-sizing: border-box;
        min-width: 36px;
        height: 100%;
        cursor: pointer;
    }

    &__icon {
        display: inline-flex;
        width: 18px;
        height: 18px;
        svg {
            width: 18px;
            height: 18px;
        }
    }

    &__period {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    &__reset {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-right: 6px;
        border-radius: 7px;
        opacity: .7;
        cursor: pointer;
        transition: opacity .15s ease, background-color .15s ease;
        svg {
            width: 16px;
            height: 16px;
        }
        &:hover {
            opacity: 1;
            background: var(--blue-bright-background);
        }
    }

    // Range picked: the button grows into a single tinted control — icon, dates, reset.
    &--applied {
        &, &:hover {
            color: var(--blue-bright);
            background: var(--blue-bright-background);
        }
        // The whole control shouldn't shrink when only the cross is pressed:
        &:active {
            transform: none;
        }
        .calendar-button__main {
            padding: 0 6px 0 10px;
            &:active {
                opacity: .8;
            }
        }
    }

    &--open {
        &, &:hover {
            color: var(--blue-bright);
            background: var(--blue-bright-background);
        }
    }
}

@media screen and (max-width: 480px) {
    .calendar-button {
        height: 34px;
        font-size: 12px;

        &__main {
            min-width: 32px;
            gap: 6px;
        }
        &__reset {
            margin-right: 4px;
        }
        &--applied .calendar-button__main {
            padding: 0 4px 0 8px;
        }
    }
}
</style>
